import type { CompleteTableCleaningWorkItemCommand } from "@billiard/contracts";
import { isCompleteTableCleaningWorkItemCommand } from "@billiard/contracts";

export interface TableCleaningWorkItemFrozenCommand {
  tableNumber: string;
  request: CompleteTableCleaningWorkItemCommand;
}

export type TableCleaningWorkItemRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN";

export interface TableCleaningWorkItemRecovery {
  ownerIdentityKey: string;
  command: TableCleaningWorkItemFrozenCommand;
  state: TableCleaningWorkItemRecoveryState;
}

export type TableCleaningWorkItemRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: TableCleaningWorkItemRecovery }
  | { status: "OTHER_OPERATOR" }
  | { status: "RECOVERY_STORAGE_UNAVAILABLE" };

interface StorageAccess {
  browser: boolean;
  storage: Storage | null;
}

const definitelyNoEffectAfterIdempotencyLookup = new Set([
  "FRONTDESK_OPERATING_FACT_CONFLICT",
  "IDEMPOTENCY_COLLISION",
  "ORDER_AGGREGATE_VERSION_CONFLICT",
  "TABLE_CLEANING_ALREADY_COMPLETED",
  "TABLE_CLEANING_WORK_ITEM_CHANGED",
  "TABLE_SESSION_CHANGED",
  "TABLE_VERSION_CONFLICT",
]);

const definitelyNoEffectForClaimedAttempt = new Set([
  ...definitelyNoEffectAfterIdempotencyLookup,
  "AUTH_REQUIRED",
  "AUTH_CREDENTIAL_CONFLICT",
  "AUTHORIZATION_DEFAULT_DENY",
  "CSRF_REJECTED",
  "FORBIDDEN",
  "MOBILE_SESSION_ACTIVATION_REQUIRED",
  "NATIVE_APP_VERSION_INVALID",
  "NATIVE_APP_VERSION_MISMATCH",
  "NATIVE_APP_VERSION_REQUIRED",
  "NATIVE_SECURE_SESSION_UNAVAILABLE",
  "NATIVE_SERVER_UPDATE_REQUIRED",
  "NATIVE_UPDATE_REQUIRED",
  "ORIGIN_REJECTED",
  "REQUEST_VALIDATION_FAILED",
]);

export const TABLE_CLEANING_WORK_ITEM_RECOVERY_STORAGE_KEY =
  "billiard-erp:table-cleaning-work-item-recovery:v1";

let pendingRecovery: TableCleaningWorkItemRecovery | null = null;
let dispatchingRecovery: TableCleaningWorkItemRecovery | null = null;
let hydrated = false;
let recoveryUnavailable = false;

export function tableCleaningWorkItemIdentityKey(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): string {
  return JSON.stringify([operator.tenantId, operator.storeId, operator.id]);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(record: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(record).length === keys.length && keys.every((key) => Object.hasOwn(record, key))
  );
}

function isBoundedTrimmedString(value: unknown, minimum: number, maximum: number): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    [...value].length >= minimum &&
    [...value].length <= maximum
  );
}

function isIdentityKey(value: unknown): value is string {
  if (!isBoundedTrimmedString(value, 1, 1_024)) return false;
  try {
    const identity = JSON.parse(value) as unknown;
    return (
      Array.isArray(identity) &&
      identity.length === 3 &&
      identity.every((part) => isBoundedTrimmedString(part, 1, 256))
    );
  } catch {
    return false;
  }
}

function isFrozenCommand(value: unknown): value is TableCleaningWorkItemFrozenCommand {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["tableNumber", "request"]) &&
    isBoundedTrimmedString(value.tableNumber, 1, 32) &&
    isCompleteTableCleaningWorkItemCommand(value.request)
  );
}

function immutableCommand(
  command: TableCleaningWorkItemFrozenCommand,
): TableCleaningWorkItemFrozenCommand {
  const request = structuredClone(command.request);
  Object.freeze(request.request);
  Object.freeze(request);
  return Object.freeze({ tableNumber: command.tableNumber, request });
}

function immutableRecovery(
  ownerIdentityKey: string,
  command: TableCleaningWorkItemFrozenCommand,
  state: TableCleaningWorkItemRecoveryState,
): TableCleaningWorkItemRecovery {
  return Object.freeze({
    ownerIdentityKey,
    command: immutableCommand(command),
    state,
  });
}

function storedRecovery(value: unknown): TableCleaningWorkItemRecovery | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["version", "recovery"]) ||
    value.version !== 1 ||
    !isRecord(value.recovery) ||
    !hasExactKeys(value.recovery, ["ownerIdentityKey", "command", "state"]) ||
    !isIdentityKey(value.recovery.ownerIdentityKey) ||
    !isFrozenCommand(value.recovery.command) ||
    (value.recovery.state !== "CLAIMED" && value.recovery.state !== "OUTCOME_UNKNOWN")
  ) {
    return null;
  }
  return immutableRecovery(
    value.recovery.ownerIdentityKey,
    value.recovery.command,
    value.recovery.state,
  );
}

function recoveryEquals(
  left: TableCleaningWorkItemRecovery,
  right: TableCleaningWorkItemRecovery,
): boolean {
  return (
    left.ownerIdentityKey === right.ownerIdentityKey &&
    left.state === right.state &&
    JSON.stringify(left.command) === JSON.stringify(right.command)
  );
}

function sessionStorageAccess(): StorageAccess {
  if (typeof window === "undefined") return { browser: false, storage: null };
  try {
    return {
      browser: true,
      storage: typeof sessionStorage === "undefined" ? null : sessionStorage,
    };
  } catch {
    return { browser: true, storage: null };
  }
}

function readStoredRecovery(storage: Storage): TableCleaningWorkItemRecovery | null {
  const encoded = storage.getItem(TABLE_CLEANING_WORK_ITEM_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : storedRecovery(JSON.parse(encoded) as unknown);
}

function saveStoredRecovery(recovery: TableCleaningWorkItemRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    access.storage.setItem(
      TABLE_CLEANING_WORK_ITEM_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 1, recovery }),
    );
    const stored = readStoredRecovery(access.storage);
    return stored !== null && recoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function verifyStoredRecovery(recovery: TableCleaningWorkItemRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    const stored = readStoredRecovery(access.storage);
    return stored !== null && recoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function hydrateStoredRecovery(): void {
  if (hydrated) return;
  hydrated = true;
  const access = sessionStorageAccess();
  if (!access.browser) return;
  if (!access.storage) {
    recoveryUnavailable = true;
    return;
  }
  try {
    const encoded = access.storage.getItem(TABLE_CLEANING_WORK_ITEM_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    const stored = readStoredRecovery(access.storage);
    if (!stored) {
      recoveryUnavailable = true;
      return;
    }
    const recovery =
      stored.state === "CLAIMED"
        ? immutableRecovery(stored.ownerIdentityKey, stored.command, "OUTCOME_UNKNOWN")
        : stored;
    if (stored.state === "CLAIMED" && !saveStoredRecovery(recovery)) {
      recoveryUnavailable = true;
      return;
    }
    pendingRecovery = recovery;
  } catch {
    recoveryUnavailable = true;
  }
}

export function readTableCleaningWorkItemRecovery(
  identityKey: string,
): TableCleaningWorkItemRecoveryView {
  hydrateStoredRecovery();
  if (recoveryUnavailable || !isIdentityKey(identityKey)) {
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  if (!pendingRecovery) return { status: "NONE" };
  if (pendingRecovery.ownerIdentityKey !== identityKey) return { status: "OTHER_OPERATOR" };
  return { status: "OWNED", recovery: pendingRecovery };
}

export function claimTableCleaningWorkItemRecovery(
  identityKey: string,
  command: TableCleaningWorkItemFrozenCommand,
): TableCleaningWorkItemRecoveryView {
  const current = readTableCleaningWorkItemRecovery(identityKey);
  if (current.status !== "NONE") return current;
  if (!isIdentityKey(identityKey) || !isFrozenCommand(command)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  const recovery = immutableRecovery(identityKey, command, "CLAIMED");
  if (!saveStoredRecovery(recovery)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  pendingRecovery = recovery;
  return { status: "OWNED", recovery };
}

function verifyRecoveryForDispatch(
  recovery: TableCleaningWorkItemRecovery,
  identityKey: string,
): boolean {
  hydrateStoredRecovery();
  if (
    recoveryUnavailable ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey ||
    !verifyStoredRecovery(recovery)
  ) {
    recoveryUnavailable = true;
    return false;
  }
  return true;
}

export function beginTableCleaningWorkItemDispatch(
  recovery: TableCleaningWorkItemRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery || !verifyRecoveryForDispatch(recovery, identityKey)) return false;
  dispatchingRecovery = recovery;
  return true;
}

export function finishTableCleaningWorkItemDispatch(
  recovery: TableCleaningWorkItemRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery !== recovery || recovery.ownerIdentityKey !== identityKey) return false;
  dispatchingRecovery = null;
  return true;
}

export function markTableCleaningWorkItemOutcomeUnknown(
  recovery: TableCleaningWorkItemRecovery,
  identityKey: string,
): boolean {
  if (!verifyRecoveryForDispatch(recovery, identityKey)) return false;
  const unknown =
    recovery.state === "OUTCOME_UNKNOWN"
      ? recovery
      : immutableRecovery(identityKey, recovery.command, "OUTCOME_UNKNOWN");
  if (!saveStoredRecovery(unknown)) {
    recoveryUnavailable = true;
    return false;
  }
  pendingRecovery = unknown;
  return true;
}

export function clearTableCleaningWorkItemRecovery(
  recovery: TableCleaningWorkItemRecovery,
  identityKey: string,
): boolean {
  if (
    recoveryUnavailable ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey
  ) {
    return false;
  }
  const access = sessionStorageAccess();
  if (access.browser) {
    if (!access.storage) {
      recoveryUnavailable = true;
      return false;
    }
    try {
      const stored = readStoredRecovery(access.storage);
      if (!stored || !recoveryEquals(stored, recovery)) {
        recoveryUnavailable = true;
        return false;
      }
      access.storage.removeItem(TABLE_CLEANING_WORK_ITEM_RECOVERY_STORAGE_KEY);
      if (access.storage.getItem(TABLE_CLEANING_WORK_ITEM_RECOVERY_STORAGE_KEY) !== null) {
        recoveryUnavailable = true;
        return false;
      }
    } catch {
      recoveryUnavailable = true;
      return false;
    }
  }
  pendingRecovery = null;
  return true;
}

export function tableCleaningWorkItemOutcomeIsUnknown(
  error: unknown,
  state: TableCleaningWorkItemRecoveryState,
): boolean {
  if (!isRecord(error) || typeof error.code !== "string") return true;
  return !(state === "CLAIMED"
    ? definitelyNoEffectForClaimedAttempt.has(error.code)
    : definitelyNoEffectAfterIdempotencyLookup.has(error.code));
}

export function resetTableCleaningWorkItemRecoveryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  const access = sessionStorageAccess();
  try {
    access.storage?.removeItem(TABLE_CLEANING_WORK_ITEM_RECOVERY_STORAGE_KEY);
  } catch {
    // Test cleanup also resets the in-memory fail-closed state.
  }
}

export function resetTableCleaningWorkItemRecoveryMemoryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
}
